import React, { useState, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import Header from '../../components/Header';
import Navbar from '../../components/Navbar';

// Import components
import BookTypeSelector from './components/BookTypeSelector';
import BookDetails from './components/BookDetails';
import ChapterNavigation from './components/ChapterNavigation';
import OriginalBookView from './components/OriginalBookView';
import CustomizedBookView from './components/CustomizedBookView';
import ImageModal from './components/ImageModal';

// Import hooks
import { useBookData } from './hooks/useBookData';
import { useComments } from './hooks/useComments';

// Import styles
import './styles/BookPreviewPage.css';

const ALLOWED_EMAILS = ['singhpuru06@gmail.com', 'hemantjha28@gmail.com'];

const BookPreviewPage = () => {

  const navigate = useNavigate();
  
  // User state
  const [userEmail, setUserEmail] = useState('');
  const [user, setUser] = useState(null);
  
  // UI state
  const [showBookDetails, setShowBookDetails] = useState(false);
  const [showNavigation, setShowNavigation] = useState(false);
  const [imageModalUrl, setImageModalUrl] = useState(null);
  const [generatedChapterIds, setGeneratedChapterIds] = useState(new Set());
  const [customizedChapters, setCustomizedChapters] = useState([]);
  const [customSelectedIdx, setCustomSelectedIdx] = useState(0);
  
         // Book selection state
       const [selectedBookType, setSelectedBookType] = useState('original');
       const [bookOptions, setBookOptions] = useState({ original: true, customized: false });
       
   

  // Custom hooks
         const {
         book,
         sections,
         originalBook,
         loading,
         error,
         selectedIdx,
         setSelectedIdx,
         search,
         setSearch,
         filteredChapters,
         openSections,
         setOpenSections,
         handleBookTypeSelect: hookHandleBookTypeSelect,
         startCustomization,
         handleCustomizationComplete,
         handleCustomizationError
       } = useBookData(selectedBookType);
       
       // Create a wrapper function that updates both the hook and the main component state
       const handleBookTypeSelect = async (bookType) => {
         console.log('Main component handleBookTypeSelect called with:', bookType);
         setSelectedBookType(bookType);
         await hookHandleBookTypeSelect(bookType);
       };

  const {
    comments,
    newComment,
    setNewComment,
    isLoadingComments,
    postingComment,
    commentError,
    handleCommentSubmit
  } = useComments(book, filteredChapters, selectedIdx);

  // Get user info on mount
  useEffect(() => {
    const token = localStorage.getItem('app_token');
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setUserEmail(decoded.email);
        setUser(decoded);
      } catch {
        setUserEmail('');
        setUser(null);
      }
    }
  }, []);

  // Fetch book options
  useEffect(() => {
    const fetchBookOptions = async () => {
      try {
        const token = localStorage.getItem('app_token');
        const response = await api.get('/api/books/preview-options', {
          headers: { Authorization: `Bearer ${token}` }
        });
        setBookOptions(response.data);
      } catch (error) {
        console.error('Error fetching book options:', error);
        setBookOptions({ original: true, customized: false });
      }
    };

    fetchBookOptions();
  }, []);

  // Authorization check
  if (!ALLOWED_EMAILS.includes(userEmail) && (!user || (user.role !== 'admin' && user.role !== 'reviewer'))) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#b00', fontWeight: 600 }}>Not authorized to view this page.</div>;
  }

  // Print handlers
  const handlePrintOriginal = () => {
    const currentBook = originalBook || book;
    const chapter = currentBook?.chapters?.[selectedIdx];
    if (!chapter) return;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html><head><title>${chapter.title}</title>
      <style>
        body { font-family: 'Segoe UI', Arial, sans-serif; color: #222; background: #fff; margin: 40px; }
        h2 { text-align: center; margin-bottom: 24px; }
        p { margin: 12px 0; line-height: 1.7; }
        h3, h4 { margin-top: 24px; }
        ul, ol { margin: 16px 0 16px 32px; }
      </style>
      </head><body>
      <h2>${chapter.title}</h2>
      ${chapter.content}
      </body></html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <div className="book-preview-app-container">
      <Header title="Book Preview" showBackButton={false} />
      
      <main className="book-preview-content">
        {/* Book Type Selector */}
        <BookTypeSelector
          selectedBookType={selectedBookType}
          bookOptions={bookOptions}
          onBookTypeSelect={handleBookTypeSelect}
          onToggleBookDetails={() => setShowBookDetails(v => !v)}
          onToggleNavigation={() => setShowNavigation(v => !v)}
          showBookDetails={showBookDetails}
          showNavigation={showNavigation}
        />

        {/* Book Details */}
        {book && showBookDetails && (
          <BookDetails book={book} />
        )}

        {/* Chapter Navigation - toggled */}
        {(selectedBookType === 'original' ? !!book : customizedChapters.length > 0) && showNavigation && (
          <ChapterNavigation
            sections={sections}
            filteredChapters={selectedBookType === 'original' ? filteredChapters : customizedChapters}
            selectedIdx={selectedBookType === 'original' ? selectedIdx : customSelectedIdx}
            setSelectedIdx={selectedBookType === 'original' ? setSelectedIdx : setCustomSelectedIdx}
            search={search}
            setSearch={setSearch}
            openSections={openSections}
            setOpenSections={setOpenSections}
            generatedChapterIds={generatedChapterIds}
          />
        )}

        {/* Original Book View */}
        {selectedBookType === 'original' && (
          <OriginalBookView
            book={originalBook || book}
            selectedIdx={selectedIdx}
            setSelectedIdx={setSelectedIdx}
            comments={comments}
            newComment={newComment}
            setNewComment={setNewComment}
            isLoadingComments={isLoadingComments}
            postingComment={postingComment}
            commentError={commentError}
            handleCommentSubmit={handleCommentSubmit}
            user={user}
            onGeneratedChaptersChange={(ids) => setGeneratedChapterIds(new Set(Array.from(ids)))}
            onViewAllGenerated={() => {
              setSelectedBookType('customized');
            }}
          />
        )}

        {/* Customized Book View */}
        {selectedBookType === 'customized' && (
          <CustomizedBookView
            user={user}
            selectedIdx={customSelectedIdx}
            setSelectedIdx={setCustomSelectedIdx}
            onChaptersLoaded={(chs) => {
              setCustomizedChapters(chs || []);
              if (!chs || chs.length === 0) return;
              if (customSelectedIdx >= chs.length) setCustomSelectedIdx(0);
            }}
            comments={comments}
            newComment={newComment}
            setNewComment={setNewComment}
            isLoadingComments={isLoadingComments}
            postingComment={postingComment}
            commentError={commentError}
            handleCommentSubmit={handleCommentSubmit}
          />
        )}

        {/* Print Buttons removed */}
      </main>

      <Navbar onMenuOpen={() => {}} />
      
      {/* Image Modal */}
      <ImageModal
        imageUrl={imageModalUrl}
        onClose={() => setImageModalUrl(null)}
      />
    </div>
  );
};

export default BookPreviewPage; 